import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, 
  ListChecks, Palmtree, Clock, Loader2, Trash2, LayoutGrid,
  List, CalendarDays, GanttChart, Timer, Users, User, UserPlus
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 
                'July', 'August', 'September', 'October', 'November', 'December'];

const EVENT_COLORS = {
  task: 'bg-blue-100 text-blue-800 border-blue-200',
  leave: 'bg-green-100 text-green-800 border-green-200',
  Meeting: 'bg-purple-100 text-purple-800 border-purple-200',
  Reminder: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  Deadline: 'bg-red-100 text-red-800 border-red-200',
  Other: 'bg-gray-100 text-gray-800 border-gray-200'
};

const PRIORITY_COLORS = {
  'Low': 'bg-gray-200',
  'Medium': 'bg-blue-200',
  'High': 'bg-orange-200',
  'Urgent': 'bg-red-200'
};

// Helper function to format date as YYYY-MM-DD in local timezone (not UTC)
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const MyCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('month');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'Reminder',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    all_day: true,
    priority: 'Medium',
    create_task: false
  });

  const { getAuthHeaders, currentOffice } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) {
        headers['X-Office-ID'] = currentOffice.id;
      }

      // Calculate date range based on view
      let startDate, endDate;
      if (activeView === 'today') {
        startDate = endDate = formatLocalDate(new Date());
      } else if (activeView === 'week') {
        const curr = new Date();
        const first = curr.getDate() - curr.getDay();
        const weekStart = new Date(curr.getFullYear(), curr.getMonth(), first);
        const weekEnd = new Date(curr.getFullYear(), curr.getMonth(), first + 6);
        startDate = formatLocalDate(weekStart);
        endDate = formatLocalDate(weekEnd);
      } else {
        startDate = formatLocalDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
        endDate = formatLocalDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0));
      }

      // Fetch calendar events
      const calendarResponse = await fetch(
        `${API_URL}/api/tasks/my-calendar?start_date=${startDate}&end_date=${endDate}`,
        { headers }
      );
      if (calendarResponse.ok) {
        const calendarData = await calendarResponse.json();
        setEvents(calendarData.events || []);
      }

      // Fetch tasks
      const tasksResponse = await fetch(`${API_URL}/api/tasks/my-tasks`, { headers });
      if (tasksResponse.ok) {
        const tasksData = await tasksResponse.json();
        setTasks(tasksData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentDate.getMonth(), currentDate.getFullYear(), activeView]);

  const handleAddEvent = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) {
        headers['X-Office-ID'] = currentOffice.id;
      }

      // Create event
      const response = await fetch(`${API_URL}/api/tasks/calendar-event`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          event_type: formData.event_type,
          start_date: formData.start_date,
          end_date: formData.end_date || formData.start_date,
          start_time: formData.all_day ? null : formData.start_time,
          end_time: formData.all_day ? null : formData.end_time,
          all_day: formData.all_day
        })
      });

      if (response.ok) {
        // If create_task is true, also create a task
        if (formData.create_task) {
          await fetch(`${API_URL}/api/tasks/`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              title: formData.title,
              description: formData.description,
              due_date: formData.start_date,
              priority: formData.priority,
              task_type: 'self'
            })
          });
        }

        setShowAddModal(false);
        resetForm();
        fetchData();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to add event');
      }
    } catch (error) {
      setError('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    
    try {
      const headers = getAuthHeaders();
      await fetch(`${API_URL}/api/tasks/calendar-event/${eventId}`, {
        method: 'DELETE',
        headers
      });
      fetchData();
      setSelectedEvent(null);
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      event_type: 'Reminder',
      start_date: '',
      start_time: '',
      end_date: '',
      end_time: '',
      all_day: true,
      priority: 'Medium',
      create_task: false
    });
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({ day: prevMonthLastDay - i, isCurrentMonth: false, date: new Date(year, month - 1, prevMonthLastDay - i) });
    }
    
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ day: i, isCurrentMonth: true, date: new Date(year, month, i) });
    }
    
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ day: i, isCurrentMonth: false, date: new Date(year, month + 1, i) });
    }
    
    return days;
  };

  const getWeekDays = () => {
    const curr = new Date();
    const first = curr.getDate() - curr.getDay();
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(curr.getFullYear(), curr.getMonth(), first + i);
      days.push(date);
    }
    return days;
  };

  const getEventsForDate = (date) => {
    const dateStr = formatLocalDate(date);
    const dayEvents = events.filter(event => {
      if (event.date === dateStr) return true;
      if (event.start_date && event.end_date) {
        return dateStr >= event.start_date && dateStr <= event.end_date;
      }
      return false;
    });

    // Add tasks with due dates
    const dayTasks = tasks.filter(task => {
      if (task.due_date) {
        return task.due_date.split('T')[0] === dateStr;
      }
      return false;
    }).map(task => ({
      ...task,
      type: 'task',
      title: task.title,
      date: task.due_date?.split('T')[0]
    }));

    return [...dayEvents, ...dayTasks];
  };

  const navigateMonth = (direction) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const openAddModal = (date = null) => {
    if (date) {
      setFormData({ ...formData, start_date: formatLocalDate(date) });
    }
    setShowAddModal(true);
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  // Gantt chart data preparation
  const ganttData = useMemo(() => {
    const allItems = [
      ...tasks.filter(t => t.due_date).map(t => ({
        ...t,
        type: 'task',
        startDate: t.created_at?.split('T')[0] || t.due_date?.split('T')[0],
        endDate: t.due_date?.split('T')[0]
      })),
      ...events.filter(e => e.start_date).map(e => ({
        ...e,
        startDate: e.start_date,
        endDate: e.end_date || e.start_date
      }))
    ].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
    return allItems;
  }, [tasks, events]);

  const days = getDaysInMonth();
  const weekDays = getWeekDays();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayEvents = getEventsForDate(today);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-calendar-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Calendar</h1>
          <p className="text-gray-600">View your tasks, events, and schedule</p>
        </div>
        <Button onClick={() => openAddModal()} data-testid="add-event-button">
          <Plus className="h-4 w-4 mr-2" />
          Add Event
        </Button>
      </div>

      {/* View Tabs */}
      <Tabs value={activeView} onValueChange={setActiveView} className="w-full">
        <TabsList className="grid grid-cols-6 mb-4">
          <TabsTrigger value="today" className="flex items-center gap-1" data-testid="view-today">
            <Clock className="h-4 w-4" />
            Today
          </TabsTrigger>
          <TabsTrigger value="week" className="flex items-center gap-1" data-testid="view-week">
            <CalendarDays className="h-4 w-4" />
            Week
          </TabsTrigger>
          <TabsTrigger value="month" className="flex items-center gap-1" data-testid="view-month">
            <LayoutGrid className="h-4 w-4" />
            Month
          </TabsTrigger>
          <TabsTrigger value="schedule" className="flex items-center gap-1" data-testid="view-schedule">
            <List className="h-4 w-4" />
            Schedule
          </TabsTrigger>
          <TabsTrigger value="timeline" className="flex items-center gap-1" data-testid="view-timeline">
            <Timer className="h-4 w-4" />
            Timeline
          </TabsTrigger>
          <TabsTrigger value="gantt" className="flex items-center gap-1" data-testid="view-gantt">
            <GanttChart className="h-4 w-4" />
            Gantt
          </TabsTrigger>
        </TabsList>

        {/* Today View */}
        <TabsContent value="today">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5" />
                {today.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {todayEvents.length > 0 ? (
                <div className="space-y-3">
                  {todayEvents.map((event, idx) => (
                    <div key={idx} className={`p-4 rounded-lg border ${EVENT_COLORS[event.type] || EVENT_COLORS.Other}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {event.type === 'task' ? <ListChecks className="h-5 w-5" /> : <CalendarIcon className="h-5 w-5" />}
                          <div>
                            <p className="font-semibold">{event.title}</p>
                            {event.description && <p className="text-sm opacity-80">{event.description}</p>}
                          </div>
                        </div>
                        <Badge variant="outline">{event.type}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No events or tasks scheduled for today</p>
                  <Button className="mt-4" onClick={() => openAddModal(today)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Event
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Week View */}
        <TabsContent value="week">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle>This Week</CardTitle>
                <Button variant="outline" onClick={goToToday}>Today</Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-2">
                {weekDays.map((date, idx) => {
                  const dayEvents = getEventsForDate(date);
                  const isCurrentDay = isToday(date);
                  return (
                    <div 
                      key={idx} 
                      className={`min-h-[200px] p-2 rounded-lg border cursor-pointer
                        ${isCurrentDay ? 'border-blue-500 border-2 bg-blue-50' : 'border-gray-200'}
                        hover:bg-gray-50`}
                      onClick={() => openAddModal(date)}
                    >
                      <div className="text-center mb-2">
                        <p className="text-xs text-gray-500">{WEEKDAYS[idx]}</p>
                        <p className={`text-lg font-semibold ${isCurrentDay ? 'text-blue-600' : ''}`}>{date.getDate()}</p>
                      </div>
                      <div className="space-y-1">
                        {dayEvents.slice(0, 4).map((event, eventIdx) => (
                          <div
                            key={eventIdx}
                            className={`text-xs p-1 rounded truncate ${EVENT_COLORS[event.type] || EVENT_COLORS.Other}`}
                            title={event.title}
                          >
                            {event.title}
                          </div>
                        ))}
                        {dayEvents.length > 4 && (
                          <p className="text-xs text-gray-500 text-center">+{dayEvents.length - 4} more</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Month View */}
        <TabsContent value="month">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Button variant="outline" size="icon" onClick={() => navigateMonth(-1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <h2 className="text-xl font-semibold min-w-[200px] text-center">
                    {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </h2>
                  <Button variant="outline" size="icon" onClick={() => navigateMonth(1)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                <Button variant="outline" onClick={goToToday}>Today</Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-1 mb-2">
                {WEEKDAYS.map(day => (
                  <div key={day} className="text-center py-2 text-sm font-medium text-gray-500">{day}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {days.map((dayObj, idx) => {
                  const dayEvents = getEventsForDate(dayObj.date);
                  const isTodayDate = isToday(dayObj.date);
                  return (
                    <div
                      key={idx}
                      className={`min-h-[100px] p-2 rounded-lg border cursor-pointer transition-colors
                        ${dayObj.isCurrentMonth ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800'}
                        ${isTodayDate ? 'border-blue-500 border-2' : 'border-gray-200'}
                        hover:bg-gray-50`}
                      onClick={() => openAddModal(dayObj.date)}
                    >
                      <div className={`text-sm font-medium mb-1 ${
                        !dayObj.isCurrentMonth ? 'text-gray-400' : isTodayDate ? 'text-blue-600' : ''
                      }`}>
                        {dayObj.day}
                      </div>
                      <div className="space-y-1">
                        {dayEvents.slice(0, 3).map((event, eventIdx) => (
                          <div
                            key={eventIdx}
                            className={`text-xs px-1 py-0.5 rounded truncate border ${EVENT_COLORS[event.type] || EVENT_COLORS.Other}`}
                            onClick={(e) => { e.stopPropagation(); setSelectedEvent(event); }}
                          >
                            {event.title?.slice(0, 15)}
                          </div>
                        ))}
                        {dayEvents.length > 3 && (
                          <div className="text-xs text-gray-500 pl-1">+{dayEvents.length - 3} more</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Schedule View (List) */}
        <TabsContent value="schedule">
          <Card>
            <CardHeader>
              <CardTitle>Upcoming Schedule</CardTitle>
            </CardHeader>
            <CardContent>
              {[...events, ...tasks.filter(t => t.due_date)]
                .sort((a, b) => new Date(a.date || a.due_date || a.start_date) - new Date(b.date || b.due_date || b.start_date))
                .slice(0, 20)
                .map((item, idx) => {
                  const itemDate = item.date || item.due_date || item.start_date;
                  return (
                    <div key={idx} className="flex items-center justify-between p-3 border-b last:border-0 hover:bg-gray-50">
                      <div className="flex items-center gap-4">
                        <div className="w-16 text-center">
                          <p className="text-xs text-gray-500">
                            {new Date(itemDate).toLocaleDateString('en-US', { month: 'short' })}
                          </p>
                          <p className="text-2xl font-bold">{new Date(itemDate).getDate()}</p>
                        </div>
                        <div>
                          <p className="font-medium">{item.title}</p>
                          <p className="text-sm text-gray-500">{item.description?.slice(0, 50)}</p>
                        </div>
                      </div>
                      <Badge className={EVENT_COLORS[item.type || item.event_type] || EVENT_COLORS.Other}>
                        {item.type || item.event_type || 'Task'}
                      </Badge>
                    </div>
                  );
                })}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Timeline View */}
        <TabsContent value="timeline">
          <Card>
            <CardHeader>
              <CardTitle>Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                {ganttData.slice(0, 15).map((item, idx) => (
                  <div key={idx} className="relative flex items-start gap-4 pb-6">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center z-10 ${
                      item.type === 'task' ? 'bg-blue-100' : 'bg-purple-100'
                    }`}>
                      {item.type === 'task' ? <ListChecks className="h-6 w-6 text-blue-600" /> : <CalendarIcon className="h-6 w-6 text-purple-600" />}
                    </div>
                    <div className="flex-1 pt-2">
                      <p className="text-xs text-gray-500">{new Date(item.startDate).toLocaleDateString()}</p>
                      <p className="font-semibold">{item.title}</p>
                      {item.description && <p className="text-sm text-gray-600">{item.description}</p>}
                      <div className="flex gap-2 mt-2">
                        <Badge className={EVENT_COLORS[item.type || item.event_type] || EVENT_COLORS.Other}>
                          {item.type || item.event_type}
                        </Badge>
                        {item.priority && <Badge className={PRIORITY_COLORS[item.priority]}>{item.priority}</Badge>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Gantt Chart View */}
        <TabsContent value="gantt">
          <Card>
            <CardHeader>
              <CardTitle>Gantt Chart</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <div className="min-w-[800px]">
                  {/* Gantt Header - Days */}
                  <div className="flex border-b pb-2 mb-4">
                    <div className="w-48 shrink-0 font-medium">Task/Event</div>
                    <div className="flex-1 flex">
                      {Array.from({ length: 14 }, (_, i) => {
                        const date = new Date();
                        date.setDate(date.getDate() + i);
                        return (
                          <div key={i} className="flex-1 text-center text-xs">
                            <p className="text-gray-500">{WEEKDAYS[date.getDay()]}</p>
                            <p className="font-medium">{date.getDate()}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Gantt Rows */}
                  {ganttData.slice(0, 10).map((item, idx) => {
                    const startDate = new Date(item.startDate);
                    const endDate = new Date(item.endDate || item.startDate);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    
                    const daysDiff = Math.floor((startDate - today) / (1000 * 60 * 60 * 24));
                    const duration = Math.max(1, Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1);
                    const startOffset = Math.max(0, Math.min(14, daysDiff));
                    const barWidth = Math.min(14 - startOffset, duration);
                    
                    return (
                      <div key={idx} className="flex items-center mb-2">
                        <div className="w-48 shrink-0 truncate pr-2" title={item.title}>
                          <p className="text-sm font-medium truncate">{item.title}</p>
                          <p className="text-xs text-gray-500">{item.type || 'Event'}</p>
                        </div>
                        <div className="flex-1 relative h-8 bg-gray-50 rounded">
                          {startOffset < 14 && barWidth > 0 && (
                            <div
                              className={`absolute h-6 top-1 rounded ${
                                item.type === 'task' ? 'bg-blue-400' : 'bg-purple-400'
                              } ${item.status === 'Completed' ? 'opacity-50' : ''}`}
                              style={{
                                left: `${(startOffset / 14) * 100}%`,
                                width: `${(barWidth / 14) * 100}%`,
                                minWidth: '20px'
                              }}
                            >
                              <span className="text-xs text-white px-1 truncate block leading-6">
                                {item.title?.slice(0, 15)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Legend */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-blue-400"></div>
              <span className="text-sm">Tasks</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-green-400"></div>
              <span className="text-sm">Leave</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-purple-400"></div>
              <span className="text-sm">Meetings</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-yellow-400"></div>
              <span className="text-sm">Reminders</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-red-400"></div>
              <span className="text-sm">Deadlines</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Event Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent data-testid="add-event-modal">
          <DialogHeader>
            <DialogTitle>Add Event</DialogTitle>
            <DialogDescription>Create an event or task on your calendar</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddEvent} className="space-y-4">
            {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
            
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Enter event title"
                required
                data-testid="event-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Event Type</Label>
              <Select value={formData.event_type} onValueChange={(v) => setFormData({...formData, event_type: v})}>
                <SelectTrigger data-testid="event-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Meeting">Meeting</SelectItem>
                  <SelectItem value="Reminder">Reminder</SelectItem>
                  <SelectItem value="Deadline">Deadline</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  required
                  data-testid="event-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Start Time {!formData.all_day && '*'}</Label>
                <Input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                  disabled={formData.all_day}
                  required={!formData.all_day}
                  data-testid="event-start-time"
                  className={formData.all_day ? 'opacity-50' : ''}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                  min={formData.start_date}
                  data-testid="event-end-date"
                />
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <Input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                  disabled={formData.all_day}
                  data-testid="event-end-time"
                  className={formData.all_day ? 'opacity-50' : ''}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="all-day"
                checked={formData.all_day}
                onChange={(e) => setFormData({
                  ...formData, 
                  all_day: e.target.checked,
                  start_time: e.target.checked ? '' : formData.start_time,
                  end_time: e.target.checked ? '' : formData.end_time
                })}
                className="rounded"
                data-testid="event-all-day"
              />
              <Label htmlFor="all-day" className="font-normal cursor-pointer flex items-center gap-1">
                <Clock className="h-4 w-4" />
                All Day Event
              </Label>
            </div>

            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Add a description..."
                rows={3}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="create-task"
                checked={formData.create_task}
                onChange={(e) => setFormData({...formData, create_task: e.target.checked})}
                className="rounded"
              />
              <Label htmlFor="create-task" className="font-normal cursor-pointer">
                Also create as a task
              </Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowAddModal(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-event-button">
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Add Event
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedEvent.title}
                {selectedEvent.is_overdue && <Badge className="bg-red-100 text-red-800 text-xs">Overdue</Badge>}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className={EVENT_COLORS[selectedEvent.type || selectedEvent.event_type] || EVENT_COLORS.Other}>
                  {selectedEvent.type || selectedEvent.event_type}
                </Badge>
                {selectedEvent.status && (
                  <Badge className={
                    selectedEvent.status === 'To Do' ? 'bg-slate-200 text-slate-700' :
                    selectedEvent.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                    selectedEvent.status === 'Completed' ? 'bg-green-100 text-green-800' :
                    selectedEvent.status === 'On Hold' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }>
                    {selectedEvent.status}
                  </Badge>
                )}
                {selectedEvent.priority && (
                  <Badge className={
                    selectedEvent.priority === 'Urgent' ? 'bg-red-100 text-red-800' :
                    selectedEvent.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                    selectedEvent.priority === 'Medium' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }>
                    {selectedEvent.priority}
                  </Badge>
                )}
              </div>
              
              {selectedEvent.description && (
                <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                  <p className="text-sm text-gray-700 dark:text-gray-300">{selectedEvent.description}</p>
                </div>
              )}
              
              <div className="text-sm text-gray-500 space-y-2">
                <p className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  {new Date(selectedEvent.date || selectedEvent.start_date || selectedEvent.due_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  {selectedEvent.end_date && selectedEvent.end_date !== selectedEvent.start_date && (
                    <span> - {new Date(selectedEvent.end_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  )}
                </p>
                {(selectedEvent.start_time || selectedEvent.end_time) && (
                  <p className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    {selectedEvent.start_time && <span>{selectedEvent.start_time}</span>}
                    {selectedEvent.start_time && selectedEvent.end_time && <span>-</span>}
                    {selectedEvent.end_time && <span>{selectedEvent.end_time}</span>}
                  </p>
                )}
                {selectedEvent.all_day && !selectedEvent.start_time && (
                  <p className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    All Day
                  </p>
                )}
                
                {/* Task-specific details */}
                {selectedEvent.type === 'task' && (
                  <>
                    {selectedEvent.created_by_name && (
                      <p className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        Created by: {selectedEvent.created_by_name}
                      </p>
                    )}
                    {selectedEvent.assigned_by_name && (
                      <p className="flex items-center gap-2">
                        <UserPlus className="h-4 w-4" />
                        Assigned by: {selectedEvent.assigned_by_name}
                      </p>
                    )}
                    {selectedEvent.collaborators?.length > 0 && (
                      <p className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        {selectedEvent.collaborators.length} collaborator(s)
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
            <DialogFooter>
              {selectedEvent.id && selectedEvent.type !== 'task' && selectedEvent.type !== 'leave' && (
                <Button variant="destructive" onClick={() => handleDeleteEvent(selectedEvent.id)}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              )}
              <Button variant="outline" onClick={() => setSelectedEvent(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default MyCalendar;
